import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  keyframes,
  LinearProgress,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PeopleIcon from '@mui/icons-material/People';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';

// Animation for bars growing
const growBar = keyframes`
  from {
    transform: scaleY(0);
  }
  to {
    transform: scaleY(1);
  }
`;

// Animated bar component
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const AnimatedBar: React.FC<{ height: number; delay: number; color: string }> = ({ height, delay, color }) => {
  return (
    <Box
      sx={{
        width: '100%',
        height: `${height}%`,
        bgcolor: color,
        borderRadius: '3px 3px 0 0',
        transformOrigin: 'bottom',
        animation: `${growBar} 0.6s ease-out ${delay}s forwards`,
        transform: 'scaleY(0)',
      }}
    />
  );
};

const kpiData = [
  {
    label: 'Revenue This Month',
    value: '$124,500',
    change: '+18.2%',
    isPositive: true,
    icon: AttachMoneyIcon,
    color: '#008080'
  },
  {
    label: 'Active Customers',
    value: '1,247',
    change: '+42 new',
    isPositive: true,
    icon: PeopleIcon,
    color: '#F4A261'
  },
  {
    label: 'Total Orders',
    value: '3,891',
    change: '+12.5%',
    isPositive: true,
    icon: ShoppingCartIcon,
    color: '#003366'
  },
  {
    label: 'Avg Order Value',
    value: '$172',
    change: '-2.1%',
    isPositive: false,
    icon: ReceiptIcon,
    color: '#008080'
  },
];

const monthlyRevenue = [
  { month: 'Jan', value: 82000 },
  { month: 'Feb', value: 91000 },
  { month: 'Mar', value: 87000 },
  { month: 'Apr', value: 102000 },
  { month: 'May', value: 98000 },
  { month: 'Jun', value: 115000 },
  { month: 'Jul', value: 108000 },
  { month: 'Aug', value: 124500 },
];

const topProducts = [
  { name: 'Premium Widget Pro', revenue: '$32,450', units: 245, growth: '+24%' },
  { name: 'Standard Widget', revenue: '$28,100', units: 412, growth: '+18%' },
  { name: 'Widget Starter Pack', revenue: '$19,800', units: 380, growth: '+12%' },
  { name: 'Enterprise Solution', revenue: '$15,200', units: 28, growth: '+45%' },
  { name: 'Widget Accessories', revenue: '$12,950', units: 890, growth: '+8%' },
];

const recentInsights = [
  { type: 'opportunity', title: 'Sales Spike Detected', description: 'Widget Pro sales increased 24% this week. Consider increasing inventory.' },
  { type: 'warning', title: 'Cash Flow Alert', description: 'Accounts receivable aging over 60 days increased by 15%.' },
  { type: 'info', title: 'Seasonal Trend', description: 'Historical data suggests 30% revenue increase expected next month.' },
];

const salesByRegion = [
  { region: 'Northeast', value: 35, color: '#008080' },
  { region: 'Southeast', value: 28, color: '#F4A261' },
  { region: 'Midwest', value: 22, color: '#003366' },
  { region: 'West', value: 15, color: '#BBCDE2' },
];

const SampleDashboard: React.FC = () => {
  const [animationKey, setAnimationKey] = useState(0);
  const maxRevenue = Math.max(...monthlyRevenue.map(d => d.value));

  // Re-trigger animation periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationKey(prev => prev + 1);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box>
      <Typography variant="h4" sx={{ color: 'secondary.main', fontWeight: 700, mb: 1 }}>
        Dashboard
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 3 }}>
        Welcome back! Here&apos;s your business at a glance.
      </Typography>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {kpiData.map((kpi, index) => {
          const IconComponent = kpi.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                      {kpi.label}
                    </Typography>
                    <Box sx={{
                      bgcolor: `${kpi.color}15`,
                      p: 1,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <IconComponent sx={{ color: kpi.color, fontSize: 20 }} />
                    </Box>
                  </Box>
                  <Typography variant="h4" sx={{ color: 'secondary.main', fontWeight: 700, mb: 1 }}>
                    {kpi.value}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {kpi.isPositive ? (
                      <TrendingUpIcon sx={{ color: 'success.main', fontSize: 18, mr: 0.5 }} />
                    ) : (
                      <TrendingDownIcon sx={{ color: 'warning.main', fontSize: 18, mr: 0.5 }} />
                    )}
                    <Typography sx={{ color: kpi.isPositive ? 'success.main' : 'warning.main', fontWeight: 600, fontSize: '0.875rem' }}>
                      {kpi.change}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', ml: 1 }}>
                      vs last month
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Revenue Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, border: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 700 }}>
                  Monthly Revenue
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Year-to-date performance
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ color: 'success.main' }} />
                <Typography sx={{ color: 'success.main', fontWeight: 600 }}>
                  +18.2% YTD
                </Typography>
              </Box>
            </Box>

            {/* Animated Bar Chart */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 180, mb: 2 }} key={animationKey}>
              {monthlyRevenue.map((data, i) => (
                <Box key={data.month} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                  <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 600, mb: 0.5 }}>
                    ${(data.value / 1000).toFixed(0)}k
                  </Typography>
                  <Box sx={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end' }}>
                    <Box
                      sx={{
                        width: '100%',
                        height: `${(data.value / maxRevenue) * 100}%`,
                        bgcolor: i === monthlyRevenue.length - 1 ? '#F4A261' : '#008080',
                        borderRadius: '3px 3px 0 0',
                        transformOrigin: 'bottom',
                        animation: `${growBar} 0.6s ease-out ${i * 0.1}s forwards`,
                        transform: 'scaleY(0)',
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1 }}>
                    {data.month}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Sales by Region */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
            <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 700, mb: 3 }}>
              Sales by Region
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {salesByRegion.map((region) => (
                <Box key={region.region}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: 'secondary.main' }}>
                      {region.region}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'secondary.main', fontWeight: 600 }}>
                      {region.value}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={region.value}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: region.color,
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Top Products */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, border: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 700, mb: 2 }}>
              Top Products
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Product</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Revenue</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Units</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Growth</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topProducts.map((product, index) => (
                    <TableRow key={product.name} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: index === 0 ? 'accent.main' : 'primary.main'
                          }} />
                          {product.name}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{product.revenue}</TableCell>
                      <TableCell align="right">{product.units}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={product.growth}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(46, 125, 50, 0.1)',
                            color: 'success.main',
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* AI Insights */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
            <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 700, mb: 2 }}>
              AI Insights
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {recentInsights.map((insight, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    bgcolor: insight.type === 'opportunity' ? 'rgba(46, 125, 50, 0.08)' : insight.type === 'warning' ? 'rgba(237, 108, 2, 0.08)' : 'rgba(2, 136, 209, 0.08)',
                    borderRadius: 1,
                    borderLeft: 4,
                    borderColor: insight.type === 'opportunity' ? 'success.main' : insight.type === 'warning' ? 'warning.main' : 'info.main',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: 'secondary.main', fontWeight: 600, mb: 0.5 }}>
                    {insight.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {insight.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SampleDashboard;
